import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { TeacherDashboardFilters } from './TeacherDashboardFilters';
import { useSideNavActive } from '../LayoutWrapper/SideNavLayoutContext';
import { DailySnapshot, DailySnapshotQueryFilters } from './DailySnapshot';
import { FC, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WeeklySnapshot, WeeklySnapshotQueryFilters } from './WeeklySnapshot';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';
import { SkillChallengeAttemptedTable } from './SkillChallengeAttempted/SkillChallengeAttemptedTable';
import { selectedTeacherDashboardFilter } from '../../redux/reducers/teacherDashboardFiltersSlice';
import { startOfWeek, endOfWeek, format } from 'date-fns';
import { getDateByUserTimezone } from '../../businessLogicService/getDateByUserTimezone';
import { CustomBranding, mapCustomBranding, Roles } from '../../models/User';
import { selectedExitSlipModal } from '../../redux/reducers/exitSlipModalSlice';
import { PreloadedQuery, fetchQuery, useLazyLoadQuery, useQueryLoader, useRelayEnvironment } from 'react-relay';
import { useStoreFirstRefresh } from '../../custom-hooks/useStoreFirstRefresh';
import { selectedDiagnosticReset } from '../../redux/reducers/diagnosticResetSlice';
import { getAllClassExitSlip, getClassExitSlipsByFilter } from '../../graphQLOperations/ExitSlip';
import { ExitSlipClassExitSlipsQuery } from '../../graphQLOperations/__generated__/ExitSlipClassExitSlipsQuery.graphql';
import ExitSlipSnapshotWrapper from './ExitSlip/ExitSlipSnapshotWrapper';
import { TeacherDashboardFilterAdmin } from './TeacherDashboardFiltersAdmin';
import { ExitSlipAllClassExitSlipQuery } from '../../graphQLOperations/__generated__/ExitSlipAllClassExitSlipQuery.graphql';
import TeacherDiagnosticWidgetWrapper, { widgetInitialState } from './Diagnostic/TeacherDiagnosticWidgetWrapper';
import { DiagnosticWidgetType } from './Diagnostic/TeacherDiagnosticWidget';
import { TeacherDiagnosticDashboardWrapper } from './Diagnostic/TeacherDiagnosticDashboardWrapper';
import { getWidgetUserAction } from '../../graphQLOperations/UserAction';
import { UserActionWidgetActionQuery } from '../../graphQLOperations/__generated__/UserActionWidgetActionQuery.graphql';
import {
  buildWidgetState,
  getHiddenDiagnosticWidgetClassIds,
  HIDE_DIAGNOSTIC_WIDGET_ACTION_TYPE_ID,
} from './Diagnostic/helper/widgetHelper';
import { Toast } from 'primereact/toast';
import { CircleCheck, X } from 'lucide-react';
import {
  getDiagnosticStatusByClassReport,
  getDiagnosticStatusByTargetGroupReport,
} from '../../graphQLOperations/Diagnostics';
import { DiagnosticsStatusByClassReportQuery } from '../../graphQLOperations/__generated__/DiagnosticsStatusByClassReportQuery.graphql';
import { DiagnosticsStatusByTargetGroupReportQuery } from '../../graphQLOperations/__generated__/DiagnosticsStatusByTargetGroupReportQuery.graphql';
import { UnitProgressWidget } from './UnitProgressWidget';
import { DashboardStudentsRosterQuery$data } from '../../graphQLOperations/__generated__/DashboardStudentsRosterQuery.graphql';
import TeacherDashboardDiagnosticEmptyWrapper from './WeeklyHistory/TeacherDashboardDiagnosticEmptyWrapper';
import TeacherDashboardWidgetNoClasses from './TeacherDashboardWidgetNoClasses';
import TeacherDashboardWidgetEmpty, { TeacherDashboardWidgetType } from './TeacherDashboardWidgetEmpty';
import WeeklyHistory from './WeeklyHistory/WeeklyHistory';
import { ClassFocusOnboardingProvider, useClassFocusOnboarding } from '../../contexts/ClassFocusOnboardingContext';

export enum FilterOptionValues {
  ROADMAP,
  CLASSES,
}

interface Props {
  hasUserClassWithActiveStudent?: DashboardStudentsRosterQuery$data['hasUserClassWithActiveStudent'];
}

export interface EmptyResultsWidgets {
  dailySnapshot: boolean;
  weeklySnapshot: boolean;
}

const SNAPSHOT_TITLE = 'tw-self-start tw-font-atkinson tw-text-xs/5 tw-font-bold tw-uppercase tw-text-neutral-700';

//the widget renders canceled tickets and the calendars free their days, so both queries ask for
//the rows the resolver drops by default
const dailyExitSlipVariables = (
  date: Date,
  classIds: ReadonlyArray<string>,
): ExitSlipClassExitSlipsQuery['variables'] => ({
  filter: {
    dueOnTo: format(date, 'yyyy-MM-dd'),
    dueOnFrom: format(date, 'yyyy-MM-dd'),
    classIds,
    includeCanceled: true,
  },
  learnosityExitSlipSessionsFilter: {
    sessionStartedOn: format(date, 'yyyy-MM-dd'),
  },
});

const allExitSlipVariables = (classIds: ReadonlyArray<string>): ExitSlipAllClassExitSlipQuery['variables'] => ({
  filter: { classIds, includeCanceled: true },
});

export const TeacherDashboard: FC<Props> = props => (
  <ClassFocusOnboardingProvider>
    <TeacherDashboardInner {...props} />
  </ClassFocusOnboardingProvider>
);

const TeacherDashboardInner: FC<Props> = ({ hasUserClassWithActiveStudent }) => {
  const teacherDashboardFilters = useAppSelector(selectedTeacherDashboardFilter);
  const exitSlipModalRedux = useAppSelector(selectedExitSlipModal);
  const diagnosticResetRedux = useAppSelector(selectedDiagnosticReset);
  const userRedux = useAppSelector(selectUser);
  const toastRef = useRef<Toast>(null);

  const miamiBrand =
    userRedux?.network?.customBranding &&
    mapCustomBranding[userRedux?.network?.customBranding] === CustomBranding.MIAMI;
  const isSiteAdmin = userRedux?.roles.some(role => role.name === Roles.SITE_ADMIN);
  const startDate = startOfWeek(new Date(), { weekStartsOn: 1 });
  const endDate = endOfWeek(new Date(), { weekStartsOn: 1 });

  const [queryFiltersWeekly, setQueryFiltersWeekly] = useState<WeeklySnapshotQueryFilters | null>(null);
  const sideNavActive = useSideNavActive();

  const [filterOption, setFilterOption] = useState<FilterOptionValues>(FilterOptionValues.ROADMAP);
  const [queryFiltersDaily, setQueryFiltersDaily] = useState<DailySnapshotQueryFilters | null>(null);
  const currentClass = teacherDashboardFilters.classes?.[0] ?? null;
  const [dismissedThisSession, setDismissedThisSession] = useState<boolean>(false);
  const [noStudentsInSite, setNoStudentsInSite] = useState<boolean>(false);
  const { anyStudentCompletedDiagnostic, classFocusCompleted, stopPulsing } = useClassFocusOnboarding();

  const activationPanelClasses = sideNavActive ? 'nc_dashboard__aside' : 'tw-w-[30%] tw-min-w-[256px] tw-max-w-[410px]';
  const mainColumnClasses = sideNavActive ? 'nc_dashboard__main' : 'tw-w-[70%]';

  const [hiddenWidgetClassIds, setHiddenWidgetClassIds] = useState<Set<string>>(new Set());
  const [dateExitSlip, setDateExitSlip] = useState<Date>(
    getDateByUserTimezone(userRedux?.activeTimeZone || '').queryDate,
  );
  // Tasks ticked during this session. `allUserActions` below is fetched once and never refetched,
  // so without remembering them here the rebuild triggered by a filter change (which flips
  // `anyStudentCompletedDiagnostic`) would un-tick whatever the teacher just completed.
  const [locallyCompleted, setLocallyCompleted] = useState<ReadonlySet<DiagnosticWidgetType>>(() => new Set());
  const handleWidgetCompleted = useCallback((widgetType: DiagnosticWidgetType) => {
    setLocallyCompleted(prev => (prev.has(widgetType) ? prev : new Set(prev).add(widgetType)));
  }, []);
  const [emptyResult, setEmptyResult] = useState<EmptyResultsWidgets>({
    dailySnapshot: false,
    weeklySnapshot: false,
  });

  const { allUserActions } = useLazyLoadQuery<UserActionWidgetActionQuery>(
    getWidgetUserAction,
    {
      id: `UserNode:${userRedux?.id || ''}`,
      action: [
        'ActionTypeNode:17',
        'ActionTypeNode:18',
        'ActionTypeNode:19',
        'ActionTypeNode:20',
        'ActionTypeNode:21',
        HIDE_DIAGNOSTIC_WIDGET_ACTION_TYPE_ID,
      ],
    },
    { fetchPolicy: 'network-only' },
  );

  // Derived while rendering rather than from an effect: the query above suspends, so
  // `allUserActions` is already resolved on the very first render. Computing the state in an
  // effect would paint the guide (and the narrower layout) for one frame before hiding it again.
  const { dismissGuide: guideAlreadyDismissed, widgetCompleted = widgetInitialState } = useMemo(() => {
    const widgetState = buildWidgetState(
      allUserActions,
      hasUserClassWithActiveStudent,
      anyStudentCompletedDiagnostic,
      classFocusCompleted,
      locallyCompleted,
    );
    // Union rather than replace: a class hidden during this session isn't in the query
    // result yet (it isn't refetched after the mutation) and must not be dropped.
    setHiddenWidgetClassIds(prev => new Set([...prev, ...getHiddenDiagnosticWidgetClassIds(allUserActions)]));
    return widgetState;
  }, [
    allUserActions,
    hasUserClassWithActiveStudent,
    anyStudentCompletedDiagnostic,
    classFocusCompleted,
    locallyCompleted,
  ]);
  // Dismissing is one-way: the mutation isn't reflected in the stale `allUserActions`, so the
  // guide has to stay gone on this session's own say-so until the next reload picks it up.
  const dismissGuide = guideAlreadyDismissed || dismissedThisSession;

  const relayEnvironment = useRelayEnvironment();
  const { refresh: refreshExitSlips, supersede: supersedeExitSlipRefresh } = useStoreFirstRefresh();
  const [queryExitSlipRef, loadExitSlipQuery] = useQueryLoader<ExitSlipClassExitSlipsQuery>(getClassExitSlipsByFilter);
  const [queryAllExitSlipRef, loadAllExitSlipQuery] =
    useQueryLoader<ExitSlipAllClassExitSlipQuery>(getAllClassExitSlip);
  const [queryDiagnosticStatusByClassReportRef, loadDiagnosticStatusByClassReportQuery] =
    useQueryLoader<DiagnosticsStatusByClassReportQuery>(getDiagnosticStatusByClassReport);
  const [queryDiagnosticStatusByTargetGoupReportRef, loadDiagnosticStatusByTargetGoupReportQuery] =
    useQueryLoader<DiagnosticsStatusByTargetGroupReportQuery>(getDiagnosticStatusByTargetGroupReport);

  useEffect(() => {
    // A filter change moves to a different class/roadmap, so any in-flight attention pulse
    // (and its coachmark) no longer points at the right place — clear it.
    stopPulsing();
  }, [teacherDashboardFilters, stopPulsing]);

  useEffect(() => {
    if (hasUserClassWithActiveStudent) {
      // Reset the empty flags whenever the filters change. Otherwise, once a widget
      // reports empty it gets unmounted, and the only code that flips the flag back to
      // false lives inside that (now unmounted) widget, so switching to a class/roadmap
      // with data would never re-mount it and it would stay empty forever.
      setEmptyResult({
        dailySnapshot: false,
        weeklySnapshot: false,
      });
      setQueryFiltersWeekly({
        targetGroupIds:
          (sideNavActive || filterOption === FilterOptionValues.ROADMAP) &&
          isSiteAdmin &&
          teacherDashboardFilters.roadmaps
            ? teacherDashboardFilters.roadmaps.map(rm => rm.id)
            : null,
        classIds:
          (sideNavActive || filterOption === FilterOptionValues.CLASSES || !isSiteAdmin) &&
          teacherDashboardFilters.classes?.length
            ? teacherDashboardFilters.classes.map(cl => cl.id)
            : null,
        siteIds:
          isSiteAdmin && teacherDashboardFilters.sites?.length ? teacherDashboardFilters.sites.map(st => st.id) : null,
        gradeLevelIds: teacherDashboardFilters.grades?.length ? teacherDashboardFilters.grades.map(cl => cl.id) : null,
        reportFromDate: startDate,
        reportToDate: endDate,
      });
      setQueryFiltersDaily({
        targetGroupIds:
          (sideNavActive || filterOption === FilterOptionValues.ROADMAP) &&
          isSiteAdmin &&
          teacherDashboardFilters.roadmaps
            ? teacherDashboardFilters.roadmaps.map(rm => rm.id)
            : null,
        classIds:
          (sideNavActive || filterOption === FilterOptionValues.CLASSES || !isSiteAdmin) &&
          teacherDashboardFilters.classes?.length
            ? teacherDashboardFilters.classes.map(cl => cl.id)
            : null,
        siteIds:
          isSiteAdmin && teacherDashboardFilters.sites?.length ? teacherDashboardFilters.sites.map(st => st.id) : null,
        gradeLevelIds: teacherDashboardFilters.grades?.length ? teacherDashboardFilters.grades.map(cl => cl.id) : null,
        reportDate: getDateByUserTimezone(userRedux?.activeTimeZone || '').queryDate,
      });
    }
  }, [teacherDashboardFilters]);

  useEffect(() => {
    //a different class or date owns the widget now, so a refresh still in flight must not swap its
    //own variables back in behind this load
    supersedeExitSlipRefresh();
    if (miamiBrand && queryFiltersDaily?.classIds && dateExitSlip) {
      loadExitSlipQuery(dailyExitSlipVariables(dateExitSlip, queryFiltersDaily.classIds));
      loadAllExitSlipQuery(allExitSlipVariables(queryFiltersDaily.classIds));
    }
  }, [loadExitSlipQuery, queryFiltersDaily, dateExitSlip]);

  useEffect(() => {
    if (queryFiltersDaily?.classIds && queryFiltersDaily.classIds.length > 0) {
      loadDiagnosticStatusByClassReportQuery({
        filter: {
          classIds: queryFiltersDaily.classIds,
        },
      });
    }
  }, [loadDiagnosticStatusByClassReportQuery, queryFiltersDaily]);

  useEffect(() => {
    if (isSiteAdmin && filterOption === FilterOptionValues.ROADMAP) {
      loadDiagnosticStatusByTargetGoupReportQuery({
        filter: {
          targetGroupIds: queryFiltersDaily?.targetGroupIds,
          siteIds: queryFiltersDaily?.siteIds,
        },
      });
    }
  }, [loadDiagnosticStatusByTargetGoupReportQuery, isSiteAdmin, filterOption, queryFiltersDaily]);

  useEffect(() => {
    // Refetch the diagnostic status report after a reset so the chart, cards and
    // modals reflect the retake (the mutation flipped retakeAssigned to true).
    if (diagnosticResetRedux.resetKey > 0 && queryFiltersDaily?.classIds && queryFiltersDaily.classIds.length > 0) {
      loadDiagnosticStatusByClassReportQuery(
        {
          filter: {
            classIds: queryFiltersDaily.classIds,
          },
        },
        { fetchPolicy: 'network-only' },
      );
    }
  }, [diagnosticResetRedux.resetKey]);

  useEffect(() => {
    //refetch query after close modal and mutation was applied
    if (
      !miamiBrand ||
      !queryFiltersDaily?.classIds ||
      !exitSlipModalRedux?.exitSlipKey ||
      exitSlipModalRedux.exitSlipKey <= 0
    )
      return;

    const daily = dailyExitSlipVariables(dateExitSlip, queryFiltersDaily.classIds);
    const all = allExitSlipVariables(queryFiltersDaily.classIds);

    //the snapshot owns the move toast, so a network-only load here would suspend the boundary that
    //renders it and detach the ref its dismissal goes through
    refreshExitSlips(
      [
        fetchQuery(relayEnvironment, getClassExitSlipsByFilter, daily).toPromise(),
        fetchQuery(relayEnvironment, getAllClassExitSlip, all).toPromise(),
      ],
      fetchPolicy => {
        loadExitSlipQuery(daily, { fetchPolicy });
        loadAllExitSlipQuery(all, { fetchPolicy });
      },
    );
  }, [exitSlipModalRedux.exitSlipKey]);

  // Hiding the diagnostic widget targets a single class, so it is only on the table when
  // exactly one is in view — never on the roadmap view or with several classes selected.
  const activeClassId = queryFiltersDaily?.classIds?.length === 1 ? queryFiltersDaily.classIds[0] : null;
  const diagnosticWidgetHidden = !!activeClassId && hiddenWidgetClassIds.has(activeClassId);

  return (
    <>
      <div className={`tw-flex tw-flex-row ${dismissGuide ? 'tw-mx-32' : 'tw-mx-20'} tw-pt-10 tw-pb-16 tw-gap-x-8`}>
        <div className={`tw-flex tw-flex-col ${dismissGuide ? 'tw-w-full' : mainColumnClasses}`}>
          {isSiteAdmin ? (
            <TeacherDashboardFilterAdmin
              hasUserClassWithActiveStudent={hasUserClassWithActiveStudent}
              filterOption={filterOption}
              setFilterOption={setFilterOption}
              noStudentsInSite={noStudentsInSite}
              setNoStudentInSite={setNoStudentsInSite}
            />
          ) : (
            !sideNavActive && (
              <TeacherDashboardFilters
                dismissGuide={dismissGuide}
                hasUserClassWithActiveStudent={hasUserClassWithActiveStudent}
              />
            )
          )}

          {!diagnosticWidgetHidden ? (
            hasUserClassWithActiveStudent && !noStudentsInSite ? (
              <Suspense fallback={<ThreeDotLoader dark />}>
                <TeacherDiagnosticDashboardWrapper
                  queryDiagnosticStatusByClassReportRef={
                    queryDiagnosticStatusByClassReportRef as PreloadedQuery<
                      DiagnosticsStatusByClassReportQuery,
                      Record<string, unknown>
                    >
                  }
                  queryDiagnosticStatusByTargetGoupReportRef={
                    queryDiagnosticStatusByTargetGoupReportRef as PreloadedQuery<
                      DiagnosticsStatusByTargetGroupReportQuery,
                      Record<string, unknown>
                    >
                  }
                  isSiteAdmin={isSiteAdmin as boolean}
                  isFilteredByRoadmap={filterOption === FilterOptionValues.ROADMAP}
                  activeClassId={activeClassId}
                  onWidgetHidden={classId => setHiddenWidgetClassIds(prev => new Set(prev).add(classId))}
                />
              </Suspense>
            ) : (
              <TeacherDashboardDiagnosticEmptyWrapper />
            )
          ) : null}
          <Suspense fallback={<ThreeDotLoader dark />}>
            <UnitProgressWidget currentClass={currentClass} />
          </Suspense>

          {hasUserClassWithActiveStudent && !noStudentsInSite ? (
            <div className="nc_block tw-flex tw-flex-col tw-w-full tw-items-center tw-mb-4 !tw-gap-6 !tw-rounded-2xl">
              <h3 className={SNAPSHOT_TITLE}>Daily Snapshot</h3>
              {miamiBrand && (
                <Suspense fallback={<ThreeDotLoader dark />}>
                  {queryFiltersDaily &&
                  ((isSiteAdmin && filterOption === FilterOptionValues.CLASSES) || !isSiteAdmin) ? (
                    <ExitSlipSnapshotWrapper
                      queryExitSlipRef={
                        queryExitSlipRef as PreloadedQuery<ExitSlipClassExitSlipsQuery, Record<string, unknown>>
                      }
                      queryAllExitSlipRef={
                        queryAllExitSlipRef as PreloadedQuery<ExitSlipAllClassExitSlipQuery, Record<string, unknown>>
                      }
                      dateExitSlip={dateExitSlip}
                      setDateExitSlip={setDateExitSlip}
                    />
                  ) : (
                    <TeacherDashboardWidgetEmpty
                      type={TeacherDashboardWidgetType.EXIT_SLIP}
                      exitTicketSiteAdmin={isSiteAdmin}
                    />
                  )}
                </Suspense>
              )}
              <Suspense fallback={<ThreeDotLoader dark />}>
                {queryFiltersDaily && !emptyResult.dailySnapshot ? (
                  <DailySnapshot
                    setEmptyResult={setEmptyResult}
                    filterOption={filterOption}
                    queryFilters={queryFiltersDaily as DailySnapshotQueryFilters}
                  />
                ) : (
                  <TeacherDashboardWidgetEmpty
                    type={
                      miamiBrand
                        ? TeacherDashboardWidgetType.FOUNDATION
                        : TeacherDashboardWidgetType.TODAY_SKILL_CHALLENGE
                    }
                  />
                )}
              </Suspense>
            </div>
          ) : (
            <TeacherDashboardWidgetNoClasses title="Daily Snapshot" />
          )}

          {hasUserClassWithActiveStudent && !noStudentsInSite ? (
            <div className="nc_block tw-flex tw-flex-col tw-w-full tw-items-center">
              <h3 className={SNAPSHOT_TITLE}>Weekly Snapshot</h3>
              <Suspense fallback={<ThreeDotLoader dark />}>
                {queryFiltersWeekly && !emptyResult.weeklySnapshot ? (
                  <WeeklySnapshot
                    setEmptyResult={setEmptyResult}
                    filterOption={filterOption}
                    queryFilters={queryFiltersWeekly as WeeklySnapshotQueryFilters}
                  />
                ) : (
                  <TeacherDashboardWidgetEmpty
                    type={TeacherDashboardWidgetType.WEEKLY_PACE}
                    action={
                      queryFiltersWeekly ? <WeeklyHistory variant="link" queryFilters={queryFiltersWeekly} /> : null
                    }
                  />
                )}
              </Suspense>
              <Suspense fallback={<ThreeDotLoader dark />}>
                {queryFiltersWeekly ? <SkillChallengeAttemptedTable filterOption={filterOption} /> : null}
              </Suspense>
            </div>
          ) : (
            <TeacherDashboardWidgetNoClasses title="Weekly Snapshot" />
          )}
        </div>

        <div className={`${dismissGuide ? 'tw-hidden' : activationPanelClasses}  tw-h-auto`}>
          <TeacherDiagnosticWidgetWrapper
            setDismissGuide={setDismissedThisSession}
            widgetCompleted={widgetCompleted}
            onWidgetCompleted={handleWidgetCompleted}
            toastRef={toastRef}
          />
        </div>
      </div>
      <Toast
        ref={toastRef}
        position="top-right"
        content={({ message }) => (
          <div className="tw-mt-[100px] tw-flex tw-flex-row tw-bg-green-600 tw-border-b-[8px] tw-border-x-[0px] tw-border-t-[0px] tw-border-solid tw-border-green-700 tw-rounded-2xl tw-p-5 tw-gap-x-2">
            <CircleCheck size={26} className="tw-text-white" />
            <div className="tw-flex tw-flex-col ">
              <span className="tw-font-bold tw-text-lg tw-font-atkinson tw-text-white">{message.summary}</span>
              <span className="tw-font-atkinson tw-text-base tw-text-white">{message.detail}</span>
            </div>
            <span className="tw-text-white tw-cursor-pointer" onClick={() => toastRef.current?.clear()}>
              {' '}
              <X size={26} />
            </span>
          </div>
        )}
      />
    </>
  );
};
